// src/stores/UserStores/userStore.ts
import { departmentTypes, moduleTypes, RoleInstance } from 'Interfaces/roleBasedAccessInterfaces';
import { UserInfoInterface } from 'Interfaces/User/userInterfaces';
import { computed, makeAutoObservable } from 'mobx';
import { toast } from 'react-toastify';
import { authServices } from 'services/authServices';
import { setUserNameFromOcta } from 'utils/anonymousUserUtils';
import {
  setAccessToken,
  setRefreshToken,
  setTokenExpiryTime,
  tokenTypes
} from 'utils/AuthUtils/tokenUtils';

export class UserStore {
  userMetadata: UserInfoInterface | null = null;
  isUserInfoLoading: boolean = false;
  isLoginLoading: boolean = false;
  isAuthenticated: boolean = localStorage.getItem(tokenTypes.access) ? true : false;
  userModulesRoles: { [key in moduleTypes]: RoleInstance[] } = {
    facts: [],
    fdp: [],
    opptracker: [],
    plm: [],
    pps: []
  };

  constructor() {
    makeAutoObservable(this);
  }

  toggleLoginLoading = () => {
    this.isLoginLoading = !this.isLoginLoading;
  };

  setIsAuthenticated = (state: boolean) => {
    this.isAuthenticated = state;
  };

  handleLogout = async () => {
    try {
      await authServices.logout();
    } catch {
      // proceed with local cleanup even if the backend call fails
    }
    setAccessToken('');
    setRefreshToken('');
    setTokenExpiryTime('');
    this.isAuthenticated = false;
  };

  isUserLoggedIn = () => {
    if (localStorage.getItem(tokenTypes.access)) {
      return true;
    }
    return false;
  };

  // Get current user's role and department - helper methods for RBAC
  @computed get userDepartment(): string {
    return this.userMetadata?.userInfo?.deptId?.departmentCode || '';
  }

  @computed get ppsUserRole(): string {
    const ppsRoles = this.userMetadata?.roles?.pps || [];
    return ppsRoles.length > 0 ? ppsRoles[0].roleId.roleCode : '';
  }

  fetchUserDetails = async () => {
    try {
      this.isUserInfoLoading = true;
      this.userMetadata = await authServices.getUserInfoAndRoles();
      const tempUserRoles: { [key in moduleTypes]: RoleInstance[] } = {
        facts: [],
        fdp: [],
        opptracker: [],
        plm: [],
        pps: []
      };

      if (this.userMetadata) {
        // Store user emai in local storage so that we can directly use it for yjs collaboration
        setUserNameFromOcta(this.userMetadata.userInfo.email);
        // getOrCreateAnonymousUser();

        if (this.userMetadata.roles.pps) {
          this.userMetadata.roles.pps.forEach(role => {
            if (role) {
              tempUserRoles.pps.push({
                role: role.roleId.roleCode,
                department: this.userMetadata?.userInfo.deptId.departmentCode as departmentTypes
              });
            }
          });
        }

        // Uncomment and modify other role processing if needed
        // Uncomment these sections if you need to handle other modules as well
        // if (this.userMetadata.roles.facts) {
        //   this.userMetadata.roles.facts.forEach((role) => {
        //     if (role)
        //       tempUserRoles.facts.push({
        //         role: role.roleId.roleCode as roleTypes,
        //         department: this.userMetadata?.userInfo.deptId.departmentCode as departmentTypes,
        //       });
        //   });
        // }

        // Similar for other modules
        // ...
      }

      this.userModulesRoles = tempUserRoles;
    } catch (error) {
      toast.error('Some error occurred while fetching the user information');
    } finally {
      this.isUserInfoLoading = false;
    }
  };
}
